// ============================================================================
// Patente del auto que alguien quiere vender (ticket #90).
//
// La pide el formulario de compra directa en modo tasador: con la patente, la
// tasación sale con el año y la versión reales del auto en vez de los que la
// persona recuerda. La consulta a GetAPI la hace la edge `public-appraisal`;
// acá sólo se limpia lo que se escribió.
// ============================================================================

import { isBrasil } from './country';

/**
 * Placa brasileña ya limpia (mayúsculas, sin guion ni espacios): la Mercosul
 * (ABC1D23) o la vieja de 3 letras y 4 números (ABC1234).
 */
const PLACA_BR = /^([A-Z]{3}\d{4}|[A-Z]{3}\d[A-Z]\d{2})$/;

/**
 * Deja la patente en el formato que entienden la edge y GetAPI: mayúsculas y
 * sólo letras y números. "ab-cd·12" → "ABCD12".
 *
 * Devuelve null si lo que queda no puede ser una patente: menos de 5 o más de
 * 8 caracteres, o sin letras o sin números. Cubre las chilenas de auto (AA1000,
 * BBBB10), las de moto (BBB10, BBB100) y las del Mercosur (ABC1D23).
 *
 * Con `country` de Brasil valida la placa brasileña (ver PLACA_BR). Sin país
 * (o Chile) es exactamente la validación de siempre.
 */
export function normalizarPatente(
  v: unknown,
  country?: string | null
): string | null {
  const limpia = String(v ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  // Brasil: sólo placa brasileña. La rama chilena de abajo queda como siempre.
  if (isBrasil(country)) return PLACA_BR.test(limpia) ? limpia : null;
  if (limpia.length < 5 || limpia.length > 8) return null;
  if (!/[A-Z]/.test(limpia) || !/[0-9]/.test(limpia)) return null;
  return limpia;
}

/**
 * Notas del lead con la patente y la versión arriba.
 *
 * La patente NO se guarda en el auto borrador del lead a propósito: el admin
 * cuenta ese borrador como un auto activo del inventario, y cuando la automotora
 * quisiera cargar el auto de verdad le saltaría "ya existe un vehículo con esa
 * patente". En las notas la ve el vendedor en la ficha y en el correo de aviso.
 */
export function notasConPatente(
  mensaje: string,
  patente: string,
  version: string,
  country?: string | null
): string {
  // Las notas las lee la automotora: en Brasil, en portugués.
  const br = isBrasil(country);
  const cabecera = [
    br ? `Placa: ${patente}` : `Patente: ${patente}`,
    version.trim()
      ? br
        ? `Versão: ${version.trim()}`
        : `Versión: ${version.trim()}`
      : '',
  ]
    .filter(Boolean)
    .join('\n');
  const resto = mensaje.trim();
  return resto ? `${cabecera}\n\n${resto}` : cabecera;
}
